import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Alert
} from '@mui/material';
import { useLanguage } from '../../contexts/LanguageContext';
import { createUser } from '../../api/admin';

interface UserCreateDialogProps {
  open: boolean;
  onClose: (refresh?: boolean) => void;
}

const UserCreateDialog: React.FC<UserCreateDialogProps> = ({ open, onClose }) => {
  const { t } = useLanguage();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'user' | 'admin' | 'b2b-user' | 'mobile-users'>('user');
  const [initialCredits, setInitialCredits] = useState('10');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!username || !email || !password) {
      setError(t('admin.users.validation.required'));
      return;
    }

    try {
      setLoading(true);
      setError(null);
      await createUser({
        username,
        email,
        password,
        role,
        initial_credits: initialCredits ? parseInt(initialCredits) : undefined
      });
      onClose(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => onClose(false)} maxWidth="sm" fullWidth>
      <DialogTitle>{t('admin.users.addUser')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <TextField
          label={t('admin.users.columns.username')}
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label={t('admin.users.columns.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          label={t('admin.users.password')}
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          fullWidth
          margin="normal"
          required
        />
        <TextField
          select
          label={t('admin.users.columns.role')}
          value={role}
          onChange={(e) => setRole(e.target.value as any)}
          fullWidth
          margin="normal"
        >
          <MenuItem value="user">user</MenuItem>
          <MenuItem value="admin">admin</MenuItem>
          <MenuItem value="b2b-user">b2b-user</MenuItem>
          <MenuItem value="mobile-users">mobile-users</MenuItem>
        </TextField>
        <TextField
          label={t('admin.users.columns.credits')}
          type="number"
          value={initialCredits}
          onChange={(e) => setInitialCredits(e.target.value)}
          fullWidth
          margin="normal"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose(false)}>{t('common.cancel')}</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {t('common.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UserCreateDialog;
